import { useRoutes } from 'react-router-dom';
import {
  CssBaseline,
  Grid,
  ThemeProvider,
  StyledEngineProvider,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LazyLoadRoute from 'components/common/LazyLoadRoute';
import { cartoTheme } from 'theme';
import routes from './routes';
import useAuth from './hooks/Auth0';
import { IntlProvider } from 'react-intl';
import { useAppState } from 'components/contexts/State';
import './App.css';
import useFirebaseAuthReady from 'hooks/useFirebaseAuthReady';
import { useEffect, useRef, useState } from 'react';
import { getUserPermissions } from 'components/views/main/common/Service';
import io from 'socket.io-client';
import { getLegendMappingByRole } from 'components/contexts/DefaultCustomLegendMapping';
import {
  deleteUploadProcessById,
  getProcessDetails,
} from 'components/views/main/data/Services/DataRepoService';
import ToasterModal from 'components/views/main/common/ToasterModal';

const GridApp = styled(Grid)(() => ({
  flex: '1 1 auto',
  flexDirection: 'column',
  overflow: 'hidden',
}));

export default function App() {
  const API_ROOT = process.env.REACT_APP_BACKEND_ROOT;
  const API_NOTIFY = process.env.REACT_APP_BACKEND_NOTIFY;

  const routing = useRoutes(routes);
  const {
    setUserRoleDetails,
    setUploadStatusDetails,
    currProcessId,
    setCustomLegendConfig,
    setFileTypeFromSocket,
    setFileNameFromSocket,
    uploadStatusDetails,
    setCurrProcessId,
    setDisabledUploadDataBtn,
    setSignedUploadURL,
    isVRUCrashesFilterActive,
    setIsVRUCrashesFilterActive,
    userRole,
    activeTab,
  } = useAppState();
  useAuth();

  const isAuthReady = useFirebaseAuthReady();

  const [isFetchingPermissions, setIsFetchingPermissions] = useState(true);

  const socketRef = useRef(null);
  const previousLegendRoleRef = useRef(null);

  useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        const res = await getUserPermissions();
        setUserRoleDetails(res);
      } catch (err) {
        console.error(err.message);
      } finally {
        setIsFetchingPermissions(false);
      }
    };
    if (isAuthReady) fetchUserPermissions();
  }, [isAuthReady, setUserRoleDetails]);

  useEffect(() => {
    const updateLegendConfig = () => {
      const resolvedRole = String(userRole ?? localStorage.getItem('userRole') ?? '')
        .trim()
        .toLowerCase();

      const roleChanged = previousLegendRoleRef.current !== resolvedRole;
      previousLegendRoleRef.current = resolvedRole;

      if (roleChanged) {
        if (resolvedRole === 'default') {
          setIsVRUCrashesFilterActive(true);
        } else {
          setIsVRUCrashesFilterActive(false);
        }
      }

      const legendMapping = getLegendMappingByRole(
        resolvedRole,
        isVRUCrashesFilterActive,
        activeTab
      );

      setCustomLegendConfig((prevConfig) => {
        const normalizedActiveTab = String(activeTab || '')
          .trim()
          .toLowerCase()
          .replaceAll('-', '_');

        if (normalizedActiveTab === 'cost_estimate' || normalizedActiveTab === 'risk_report') {
          return legendMapping.map((section) => ({
            ...section,
            section_details: (section.section_details || []).map((detail) => {
              if (detail.type === 'group') {
                return {
                  ...detail,
                  visible: false,
                  group_details: (detail.group_details || []).map((subDetail) => ({
                    ...subDetail,
                    visible: false,
                  })),
                };
              }

              return {
                ...detail,
                visible: false,
              };
            }),
          }));
        }

        if (roleChanged) return legendMapping;
        if (!prevConfig || prevConfig.length === 0) return legendMapping;
        // Helper to copy visibility recursively
        const copyVisibility = (newSection, oldSection) => {
          if (newSection.section_details && oldSection.section_details) {
            newSection.section_details.forEach((newItem, idx) => {
              const oldItem = oldSection.section_details[idx];
              if (newItem.type === 'group' && oldItem && oldItem.type === 'group') {
                // Copy group visibility
                newItem.visible = oldItem.visible;
                newItem.opacity = oldItem.opacity;
                newItem.isOpacityControlOpen = oldItem.isOpacityControlOpen;
                newItem.collpsed = oldItem.collpsed;
                // Copy group_details visibility
                if (newItem.group_details && oldItem.group_details) {
                  newItem.group_details.forEach((newGroup, gidx) => {
                    const oldGroup = oldItem.group_details[gidx];
                    if (oldGroup) {
                      newGroup.visible = oldGroup.visible;
                      newGroup.opacity = oldGroup.opacity;
                      newGroup.isOpacityControlOpen = oldGroup.isOpacityControlOpen;
                      newGroup.collpsed = oldGroup.collpsed;
                    }
                  });
                }
              } else if (newItem.type === 'layer' && oldItem && oldItem.type === 'layer') {
                // Copy layer visibility
                newItem.visible = oldItem.visible;
                newItem.opacity = oldItem.opacity;
                newItem.isOpacityControlOpen = oldItem.isOpacityControlOpen;
                newItem.collpsed = oldItem.collpsed;
              }
            });
          }
          return newSection;
        };
        // Copy visibility for each section
        return legendMapping.map((section, idx) => {
          const oldSection = prevConfig[idx];
          if (oldSection) {
            return copyVisibility(section, oldSection);
          }
          return section;
        });
      });
    };

    updateLegendConfig();

    const handleUserRoleUpdate = () => {
      updateLegendConfig();
    };
    //Listening for when Role is Updated 
    window.addEventListener('userRoleUpdated', handleUserRoleUpdate);

    return () => {
      window.removeEventListener('userRoleUpdated', handleUserRoleUpdate);
    };
  }, [
    setCustomLegendConfig,
    isVRUCrashesFilterActive,
    setIsVRUCrashesFilterActive,
    userRole,
    activeTab,
 ]);

  useEffect(() => {
    const handleGetProcessDetalis = async () => {
      try {
        const response = await getProcessDetails();
        if (
          response &&
          response?.steps?.length > 0 &&
          response?.steps?.every((item) => typeof item === 'object' && item !== null)
        ) {
          setDisabledUploadDataBtn(true);
          if (response?.process_id) {
            setCurrProcessId(response?.process_id);
            if (response?.file_type === 'document') {
              let stepsData = [...response?.steps];
              stepsData.splice(1, 0, {
                step: 'Add details',
                status: 'Not started',
                message: '',
              });

              const order = {
                Upload: 1,
                'Add details': 2,
                Finalize: 3,
              };

              stepsData.sort((a, b) => order[a.step] - order[b.step]);

              if (
                stepsData[2].step === 'Finalize' &&
                stepsData[2].status === 'Completed'
              ) {
                stepsData[1].status = 'Completed';
              }

              setUploadStatusDetails(stepsData);
            } else {
              const steps = response?.steps;

              const order = {
                Upload: 1,
                'Add details': 2,
                Finalize: 3,
              };
              steps.sort((a, b) => order[a.step] - order[b.step]);

              if (
                steps[1].step === 'Add details' &&
                steps[1].status === 'Completed' &&
                steps[2].step === 'Finalize' &&
                steps[2].status === 'Not started'
              ) {
                steps[1].status = 'In progress';
              }

              setUploadStatusDetails(steps);
            }
            setFileTypeFromSocket(response?.file_type);
            setFileNameFromSocket(response?.file_name);
          }
        }
      } catch (error) {
        console.error(error.message);
      }
    };

    if (isAuthReady) handleGetProcessDetalis();
  }, [isAuthReady]);

  useEffect(() => {
    if (
      currProcessId &&
      uploadStatusDetails &&
      uploadStatusDetails.length > 0 &&
      uploadStatusDetails.find((s) => s.step === 'Upload')?.status === 'In progress'
    ) {
      if (!socketRef.current) {
        socketRef.current = io(API_ROOT, {
          path: API_NOTIFY,
          transports: ['websocket'],
          reconnectionAttempts: 5,
          reconnectionDelay: 2000,
        });
        console.log('Connecting to server...');
        socketRef.current.on('connect', () => {
          console.log('Connected to server!');
          socketRef.current.emit('subscribe', {
            subscriptionId: currProcessId,
          });
        });
        socketRef.current.on('message', (message) => {
          console.log('Received message:', message);
          if (
            message &&
            message?.data &&
            Array.isArray(message?.data?.steps) &&
            message?.data?.steps.every(
              (item) => typeof item === 'object' && item !== null
            )
          ) {
            if (message?.data?.file_type === 'document') {
              let stepsData = [...message?.data?.steps];
              stepsData?.splice(1, 0, {
                step: 'Add details',
                status: 'Not started',
                message: '',
              });

              const order = {
                Upload: 1,
                'Add details': 2,
                Finalize: 3,
              };
              stepsData?.sort((a, b) => order[a.step] - order[b.step]);
              if (
                stepsData[2].step === 'Finalize' &&
                stepsData[2].status === 'Completed'
              ) {
                stepsData[1].status = 'Completed';
              }
              setUploadStatusDetails(stepsData);
            } else {
              const steps = message?.data?.steps;
              const order = {
                Upload: 1,
                'Add details': 2,
                Finalize: 3,
              };
              steps?.sort((a, b) => order[a.step] - order[b.step]);

              if (
                steps[1].step === 'Add details' &&
                steps[1].status === 'Completed' &&
                steps[2].step === 'Finalize' &&
                steps[2].status === 'Not started'
              ) {
                steps[1].status = 'In progress';
              }

              setUploadStatusDetails(steps);
            }
            setFileTypeFromSocket(message?.data?.file_type);
            setFileNameFromSocket(message?.data?.file_name);
            if (
              message?.data?.steps.find((s) => s.step === 'Upload')?.status ===
              'Completed'
            ) {
              if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
                console.log('Disconnected from server.');
              }
            }
          }
        });
        socketRef.current.on('connect_error', (error) => {
          console.error('Connection Error:', error);
        });
        socketRef.current.on('error', (error) => {
          console.error('Socket Error:', error);
        });
      }
    } else {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        console.log('Disconnected from server.');
      }
    }
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        console.log('Disconnected from server.');
      }
    };
  }, [currProcessId]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (currProcessId) {
        const removeUploadProcess = async (processId) => {
          try {
            await deleteUploadProcessById(processId);
            setSignedUploadURL('');
            setUploadStatusDetails([]);
            setCurrProcessId('');
            setDisabledUploadDataBtn(false);
            setFileTypeFromSocket('');
            setFileNameFromSocket('');
          } catch (err) {
            console.error(err.message);
          }
        };
        const processId = currProcessId;
        removeUploadProcess(processId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (!isAuthReady || isFetchingPermissions) {
    return (
      <LinearProgress
        sx={{ marginTop: '-8px', marginLeft: '-8px', width: 'calc(100% + 16px)' }}
      />
    );
  }

  return (
    <IntlProvider locale='en'>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={cartoTheme}>
          <CssBaseline />
          <GridApp container>
            <LazyLoadRoute>{routing}</LazyLoadRoute>
          </GridApp>
          <ToasterModal />
        </ThemeProvider>
      </StyledEngineProvider>
    </IntlProvider>
  );
}
